import { Card, CardBody, Flex, useMediaQuery, VStack } from '@chakra-ui/react';
import {
  ListingComparableSetFragment,
  ListingFragment,
  MlsListingObject,
  PropertyDetailsFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import {
  getPropertyDetails,
  transformPropertyDetailsToMlsListing,
} from '../../Listings/ListingPage/utils';
import { ExploreProvider } from '../../MapSearch/ExploreContext';
import { PrintCompColumns } from './CompColumns/PrintCompColumns';
import { ViewCompsColumns } from './CompColumns/ViewCompsColumns';
import { CompsMap } from './CompsMap/CompsMap';
import { CompsSummary } from './CompsSummary';

interface ViewCompsProps {
  listing?: ListingFragment | PublicListingFragment;
  propertyDetails?: PropertyDetailsFragment;
  comparableSet?: ListingComparableSetFragment;
  comps: ComparableState[];
  isForPdf?: boolean;
}

export const ViewComps = ({
  listing,
  propertyDetails,
  comparableSet,
  comps,
  isForPdf,
}: ViewCompsProps) => {
  const [isPrint] = useMediaQuery('print');

  const normalizedPropertyDetails = useMemo(
    () => getPropertyDetails(listing, propertyDetails),
    [listing, propertyDetails]
  );

  // If the listing is off market, we need to transform the property details to an MlsListingFragment structure.
  // This is a hack to allow PropertyDetails to be used with the ComparableColumn components.
  const mlsListing = listing
    ? listing.mlsListing
    : transformPropertyDetailsToMlsListing(propertyDetails);

  return (
    <ExploreProvider showClosedStatus skipMlsListingsSearch>
      <VStack gap={isPrint ? 4 : { base: 0, md: 4 }} width="100%">
        <Flex
          direction={isPrint ? 'row' : { base: 'column', md: 'row' }}
          gap={isPrint ? 4 : { base: 0, md: 4 }}
          height={isPrint ? 'auto' : { base: '100%', md: '40vh' }}
          position="relative"
          width="100%"
        >
          <Flex
            display={isPrint || isForPdf ? 'none' : 'flex'}
            flexBasis={isPrint ? 0 : { base: undefined, md: 0 }}
            flexGrow={isPrint ? 1 : { base: undefined, md: 1 }}
            height={isPrint ? 'auto' : { base: '40vh', md: '100%' }}
          >
            <CompsMap
              comparableSet={comparableSet}
              isMini={false}
              mainListing={normalizedPropertyDetails}
            />
          </Flex>
          <Flex flexBasis={0} flexGrow={1} height="100%">
            <Card
              borderRadius={isPrint ? '12px' : { base: '0px', md: '12px' }}
              width="100%"
            >
              <CardBody>
                <CompsSummary
                  comparableSet={comparableSet}
                  listing={normalizedPropertyDetails}
                />
              </CardBody>
            </Card>
          </Flex>
        </Flex>

        {isPrint || isForPdf ? (
          <PrintCompColumns
            comparableSet={comparableSet}
            comps={comps}
            mainMlsListing={mlsListing as MlsListingObject}
          />
        ) : (
          <ViewCompsColumns
            comparableSet={comparableSet}
            comps={comps}
            isForPdf={isForPdf}
            isPrint={isPrint}
            mainMlsListing={mlsListing as MlsListingObject}
          />
        )}
      </VStack>
    </ExploreProvider>
  );
};
